import { z } from "zod";

import { zTieBreaker } from "@/gen/realtime";
import { zRichText } from "@/utils/types";

export const zFormGamePointSystem = z.enum(["Standard", "Armageddon", "NorwayChessClassical"]);

export const zFormTrialPointSystem = z.enum(["Standard", "Double", "SumPoints"]);

const zFormTrialGames = z.union([
    z.object({
        Fixed: z.number().positive().int(),
    }),
    z.object({
        RepeatUntilDecisive: z.number().positive().int(),
    }),
]);

export const zFormTrialStep = z.object({
    point_system: zFormGamePointSystem.nullish(),
    time_control: z
        .string()
        .regex(/^\d+\+\d+$/, {
            message: "Time control must be in the format 'minutes+increment' (e.g., '10+5')",
        })
        .nullish(),
    games: zFormTrialGames.nullish(),
});

export const zFormTournamentStageKind = z.enum(["POOL", "ELIMINATION", "UNSUPPORTED"]);
export const zFormTrialFormat = z.object({
    steps: zFormTrialStep.array().min(1).optional(),
    points: zFormTrialPointSystem.optional(),
});

export const zFormTieBreakers = z.array(zTieBreaker);

export const zFormEliminationFormat = z.enum(["single", "double"]);

export const zFormPairing = z.object({
    player1_id: z.string().uuid().nullable(),
    player2_id: z.string().uuid().nullable(),
});

export const zFormEliminationSettings = z.object({
    format: zFormEliminationFormat.nullable(),
    include_third_place_match: z.boolean().default(false),
    initial_pairings: z
        .array(zFormPairing)
        .default([])
        .refine(pairings => Number.isInteger(Math.log2(pairings.length)) && pairings.length > 0, {
            message: "Number of pairings must be a power of two",
        }),
});

export const zTournamentStageFormInput = z
    .object({
        title: z.string().min(0, "Title is required"),
        description: zRichText.nullable(),
        kind: zFormTournamentStageKind,
        order: z.coerce.number().min(0, "Order must be 0 or greater"),
        trialFormat: zFormTrialFormat.nullable().default(null),
        tieBreakers: zFormTieBreakers.nullable().default(null),
        eliminationSettings: zFormEliminationSettings.nullable().default(null),
    })
    .refine(data => data.kind !== "ELIMINATION" || data.eliminationSettings !== null, {
        message: "eliminationSettings is required when kind is 'ELIMINATION'",
        path: ["eliminationSettings"],
    })
    .refine(
        data => {
            if (data.kind === "ELIMINATION" && data.eliminationSettings) {
                return data.eliminationSettings.format !== null;
            }
            return true;
        },
        {
            message: "Elimination format must be selected",
            path: ["eliminationSettings", "format"],
        }
    );

export type TournamentStageFormInput = z.infer<typeof zTournamentStageFormInput>;
export type FormTrialGames = z.infer<typeof zFormTrialGames>;
